import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { api, track } from "@/lib/api";
import { useCardView } from "@/lib/analytics";
import { MerchantNote } from "./MerchantNote";
import { AffiliateCardLink, AffiliateCardButton } from "./AffiliateCardLink";
import { SmartImage } from "@/components/site/SmartImage";

const DISCLOSURE =
  "Human Home Living may earn a commission when you purchase through links on this page, at no additional cost to you.";

export const AffiliateDisclosure = ({ testId = "affiliate-disclosure", className = "" }) => (
  <p
    data-testid={testId}
    className={`flex items-start gap-2 text-[13.5px] leading-relaxed text-[#4F4B45] ${className}`}
  >
    <ShieldCheck size={15} className="shrink-0 mt-[3px] text-sage-dark" />
    <span>{DISCLOSURE}</span>
  </p>
);

const OfferCard = ({ offer, category, placement, articleSlug, index, rel, ctaBlock }) => {
  const ref = useRef(null);
  const clickPayload = {
    event_type: "cta_click",
    cta_placement: placement,
    offer_category: category,
    article_slug: articleSlug,
    offer_id: offer.id,
    merchant: offer.merchant,
    product: offer.display_name,
  };
  useCardView(ref, {
    event_type: "product_view",
    cta_placement: placement,
    offer_category: category,
    article_slug: articleSlug,
    offer_id: offer.id,
    merchant: offer.merchant,
    product: offer.display_name,
  });

  return (
  <AffiliateCardLink
    innerRef={ref}
    href={offer.tracking_url}
    ariaLabel={`${offer.display_name} — view at ${offer.merchant}`}
    testId={`offer-card-${offer.id}`}
    trackPayload={clickPayload}
    rel={rel}
    className="bg-white border border-line border-t-[3px] border-t-sage px-6 md:px-7 py-7 flex flex-col"
  >
    {offer.image && (
      <div
        className={`-mx-6 md:-mx-7 -mt-7 mb-6 block overflow-hidden ${
          offer.image_fit === "contain" ? "aspect-[4/3] bg-[#F4F2ED]" : "aspect-[16/10]"
        }`}
      >
        <SmartImage
          src={offer.image}
          alt={offer.image_alt || offer.display_name}
          sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
          maxWidth={768}
          style={offer.image_position ? { objectPosition: offer.image_position } : undefined}
          className={`w-full h-full transition-transform duration-[900ms] ease-out group-hover:scale-[1.03] ${
            offer.image_fit === "contain" ? "object-contain p-5" : "object-cover"
          }`}
        />
      </div>
    )}
    <span
      data-testid={`offer-merchant-${offer.id}`}
      className="text-[11.5px] tracking-[0.16em] uppercase text-[#48563F] font-bold"
    >
      {offer.merchant}
      <span className="font-medium text-[#7A766F]">
        {" "}
        · {offer.kind === "product" ? "Product" : "Collection"}
      </span>
    </span>
    <h3 className="text-[19px] md:text-[20px] font-medium leading-snug text-charcoal mt-3">
      {offer.display_name}
    </h3>
    <p className="text-[16px] leading-[1.6] text-[#454545] mt-3">{offer.description}</p>
    {offer.safety_note && (
      <p
        data-testid={`offer-safety-note-${offer.id}`}
        className="text-[13.5px] leading-relaxed text-[#6E6A63] mt-3"
      >
        {offer.safety_note}
      </p>
    )}
    <div className="mt-auto pt-7">
      <AffiliateCardButton
        testId={`offer-cta-${offer.id}`}
        className={ctaBlock ? "w-full py-4 text-[12px] leading-tight" : ""}
      >
        {offer.cta_label} <ArrowUpRight size={15} />
      </AffiliateCardButton>
      <MerchantNote merchant={offer.merchant} testId={`offer-merchant-note-${offer.id}`} />
    </div>
  </AffiliateCardLink>
  );
};

export const OfferGrid = ({
  category,
  heading,
  intro,
  label,
  id = "explore-options",
  placement = "OFFER GRID",
  articleSlug,
  tone = "light",
  fillerImage,
  disclosure = false,
  rel,
  ctaBlock = false,
  singleWidthClass = "max-w-xl",
}) => {
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (!category) return;
    api
      .get("/offers/group", { params: { category } })
      .then((r) => setOffers(r.data.offers || []))
      .catch(() => {});
  }, [category]);

  if (!offers.length) return null;

  return (
    <section
      id={id}
      data-testid={`offer-grid-${category}`}
      className={`mt-14 ${tone === "light" ? "" : ""}`}
    >
      <span className="eyebrow">{heading}</span>
      {intro && (
        <p className="text-[17px] md:text-[18px] leading-[1.6] text-[#3F3F3F] mt-4 max-w-3xl">
          {intro}
        </p>
      )}
      {label && (
        <span data-testid={`offer-grid-label-${category}`} className="eyebrow block mt-8">
          {label}
        </span>
      )}
      <div
        className={`grid gap-px bg-line mt-7 ${
          offers.length === 1
            ? singleWidthClass
            : offers.length === 2
            ? "md:grid-cols-2"
            : offers.length >= 4
            ? "md:grid-cols-2"
            : "md:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {offers.map((o, i) => (
          <OfferCard
            key={o.id}
            offer={o}
            index={i}
            category={category}
            placement={placement}
            articleSlug={articleSlug}
            rel={rel}
            ctaBlock={ctaBlock}
          />
        ))}
        {fillerImage && offers.length % 2 === 1 && (
          <div
            data-testid="offer-grid-filler-image"
            className="relative bg-white overflow-hidden min-h-[260px]"
          >
            <SmartImage
              src={fillerImage.image}
              alt={fillerImage.image_alt}
              sizes="(min-width:768px) 50vw, 100vw"
              maxWidth={1024}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        )}
      </div>
      <p className="text-[13px] text-[#6E6A63] mt-5 max-w-3xl">
        Prices and availability are shown by the merchant and change over time, so we do not list
        them here.
      </p>
      {disclosure && (
        <AffiliateDisclosure testId={`offer-grid-disclosure-${category}`} className="mt-4 max-w-3xl" />
      )}
    </section>
  );
};
